import { useState } from 'react';
import { Trophy, ArrowRight, Ban, CalendarClock, Clock, Bell, CalendarCheck, Plus, Compass, AlertTriangle, ChevronDown, TrendingDown } from 'lucide-react';
import { TIERS, GOAL_STATUSES, goalProgress, isStale, isQuickWin, isWin, dueInDays, isOpenGoal, reviewState, daysUntil, daysSince, tierOf, slotLabel } from './lib';
import { useBiz } from './BizContext';
import { YearPlan } from './YearPlan';
import { StatTile, Eyebrow, Avatar, AvatarStack, Progress, fmtDate, fmtMonth } from './bits';

function Attention({ items, goals, onOpen, onOpenGoal }) {
  const open = items.filter((i) => i.status !== 'done');
  const rows = [
    ...goals.filter((g) => g.status === 'off_track').map((g) => ({ id: g.id, title: g.title, owner: g.owner_id, name: g.owner_name, k: 'Off track', s: 'This goal has slipped — it needs a recovery plan', Icon: TrendingDown, c: 'var(--rose-deep)', go: () => onOpenGoal(g.id) })),
    ...open.filter((i) => i.flags.includes('blocked')).map((i) => ({ id: i.id, title: i.title, owner: i.owner_id, name: i.owner_name, k: 'Blocked', s: i.waiting_on ? `Waiting on ${i.waiting_on}` : 'Needs unblocking', Icon: Ban, c: 'var(--rose-deep)', go: () => onOpen(i.id) })),
    ...goals.filter((g) => reviewState(g) && reviewState(g) !== 'ok').map((g) => { const n = daysUntil(g.review_date); return { id: g.id, title: g.title, owner: g.owner_id, name: g.owner_name, k: n < 0 ? 'Review overdue' : 'Review due', s: n < 0 ? `Sit-down was due ${fmtDate(g.review_date)}` : n === 0 ? 'Sit-down today' : `Sit-down ${fmtDate(g.review_date)}`, Icon: CalendarCheck, c: n < 0 ? 'var(--rose-deep)' : 'var(--sage-deep)', go: () => onOpenGoal(g.id) }; }),
    ...open.filter((i) => !i.flags.includes('blocked') && dueInDays(i) != null && dueInDays(i) <= 14).map((i) => ({ id: i.id, title: i.title, owner: i.owner_id, name: i.owner_name, k: dueInDays(i) < 0 ? 'Overdue' : 'Due soon', s: dueInDays(i) < 0 ? `${-dueInDays(i)} days overdue` : `Due ${fmtDate(i.due)}`, Icon: CalendarClock, c: 'var(--apricot-deep)', go: () => onOpen(i.id) })),
    ...open.filter((i) => isStale(i) && !i.flags.includes('blocked')).map((i) => ({ id: i.id, title: i.title, owner: i.owner_id, name: i.owner_name, k: 'Stale', s: 'Nobody has touched this in over a month', Icon: Clock, c: 'var(--ink-3)', go: () => onOpen(i.id) })),
  ];
  const [show, setShow] = useState(false);
  if (rows.length === 0) return null;
  return (
    <section className="rounded-2xl px-4 py-2.5" style={{ background: 'var(--honey-soft)', border: '1px solid rgba(127, 99, 22, 0.25)' }} data-testid="biz-attention">
      <button type="button" onClick={() => setShow((v) => !v)} className="w-full flex items-center gap-2 text-sm text-left" data-testid="biz-attention-toggle">
        <AlertTriangle className="w-4 h-4 shrink-0" style={{ color: 'var(--honey-deep)' }} />
        <span className="flex-1" style={{ color: 'var(--honey-deep)' }} data-testid="biz-attention-count"><span className="font-semibold">{rows.length} thing{rows.length === 1 ? '' : 's'} need{rows.length === 1 ? 's' : ''} attention</span> · {[...new Set(rows.map((r) => r.k))].slice(0, 3).join(', ')}</span>
        <ChevronDown className="w-4 h-4 transition-transform" style={{ color: 'var(--honey-deep)', transform: show ? 'rotate(180deg)' : 'none' }} />
      </button>
      {show && (
        <ul className="mt-2" data-testid="biz-attention-list">
          {rows.map(({ id, title, owner, name, k, s, Icon, c, go }) => (
            <li key={`${k}-${id}`} style={{ borderTop: '1px solid rgba(127, 99, 22, 0.18)' }}>
              <button type="button" onClick={go} className="w-full text-left flex items-center gap-3 py-2.5 group" data-testid={`biz-attention-${id}`}>
                <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: 'var(--paper)', color: c }}><Icon className="w-4 h-4" /></span>
                <span className="flex-1 min-w-0">
                  <span className="text-sm font-medium block truncate">{title}</span>
                  <span className="text-xs block truncate" style={{ color: 'var(--ink-3)' }}><span className="font-semibold" style={{ color: c }}>{k}</span> · {s}</span>
                </span>
                <Avatar ownerId={owner} name={name} />
                <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--ink-3)' }} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const ago = (s) => { if (!s) return null; const n = daysSince(s); return n <= 0 ? 'today' : n === 1 ? 'yesterday' : n < 30 ? `${n} days ago` : fmtDate(s); };

// Goal 30 | Status 11 | Review 16 | Last update 11 | Initiatives 11 | Target 11 | Owner 10 — fr keeps the proportions after the 24px gaps.
const COLS = 'md:grid-cols-[minmax(0,30fr)_minmax(0,11fr)_minmax(0,16fr)_minmax(0,11fr)_minmax(0,11fr)_minmax(0,11fr)_minmax(0,10fr)] md:gap-x-6';
const HEADERS = ['Goal', 'Status', 'Review', 'Last update', 'Initiatives', 'Target', 'Owner'];
const BLEED = '-mx-3 px-3 w-[calc(100%+1.5rem)]';

function Th({ children, right }) {
  return <p className={`text-[11px] font-semibold uppercase tracking-[0.04em] whitespace-nowrap ${right ? 'text-right' : ''}`} style={{ color: 'var(--ink-3)' }}>{children}</p>;
}

// Empty values collapse to a muted dash; the wording lives in the tooltip.
function Val({ value, empty, strong, testId }) {
  if (!value) return <p className="text-[13px] font-medium" style={{ color: 'var(--ink-3)' }} title={empty} data-testid={testId}>—</p>;
  return <p className="text-[13px] font-medium truncate" style={{ color: strong ? 'var(--ink)' : 'var(--ink-2)' }} title={value} data-testid={testId}>{value}</p>;
}

function StatusPill({ st, testId }) {
  return <span className="inline-flex items-center rounded-full text-xs font-semibold whitespace-nowrap" style={{ padding: '4px 10px', background: st.bg, color: st.fg }} data-testid={testId}>{st.label}</span>;
}

function ReviewCell({ g, testId }) {
  const n = daysUntil(g.review_date);
  const tone = n == null ? 'var(--ink-3)' : n < 0 ? 'var(--rose-deep)' : n <= 7 ? 'var(--honey-deep)' : 'var(--ink-2)';
  const next = n == null ? '—' : `${n < 0 ? 'Overdue' : 'Next'} ${fmtDate(g.review_date)}`;
  return (
    <div className="min-w-0" data-testid={testId}>
      <p className="text-[13px] font-medium truncate" style={{ color: tone }} title={n == null ? 'No review scheduled' : next}>{next}</p>
      <p className="text-[11px] truncate mt-0.5" style={{ color: 'var(--ink-3)' }}>Last: {g.last_reviewed_at ? fmtDate(g.last_reviewed_at) : 'never'}</p>
    </div>
  );
}

function GoalName({ g, h, p, st, sfx = '' }) {
  const slot = tierOf(g) === 'quarter' ? `Q${g.quarter}` : null;
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2 min-w-0">
        {slot && <span className="text-xs font-semibold shrink-0 px-1.5 py-0.5 rounded-md" style={{ background: h.soft, color: h.deep }}>{slot}</span>}
        <span className="text-sm font-semibold leading-snug truncate" title={g.title} data-testid={`biz-glance-title-${g.id}${sfx}`}>{g.title}</span>
        {g.provisional && <span className="biz-chip shrink-0" style={{ background: 'var(--paper-3)', color: 'var(--ink-2)' }} data-testid={`biz-glance-slot-${g.id}${sfx}`}>{slotLabel(g)} · provisional</span>}
      </div>
      <div className="flex items-center gap-2.5 mt-2">
        <div className="flex-1 min-w-0 max-w-[180px]"><Progress pct={p.pct} color={st.bar} /></div>
        <span className="text-xs font-semibold tabular-nums shrink-0" style={{ color: st.fg }} data-testid={`biz-glance-pct-${g.id}${sfx}`}>{p.pct}%</span>
      </div>
    </div>
  );
}

function GoalRow({ g, items, h, onOpenGoal, first }) {
  const p = goalProgress(g, items);
  const st = GOAL_STATUSES[g.status];
  const updated = ago(g.last_activity_at || g.updated_at);
  const inits = p.total ? `${p.done} of ${p.total} done` : null;
  const target = g.target_date ? fmtMonth(g.target_date) : null;
  return (
    <button type="button" onClick={() => onOpenGoal(g.id)} className={`block text-left rounded-xl biz-row-hover group ${BLEED}`} style={first ? undefined : { borderTop: '1px solid var(--line)' }} data-testid={`biz-glance-${g.id}`}>
      <div className={`hidden md:grid ${COLS} items-center py-4 min-h-[64px]`}>
        <GoalName g={g} h={h} p={p} st={st} />
        <div><StatusPill st={st} testId={`biz-glance-status-${g.id}`} /></div>
        <ReviewCell g={g} testId={`biz-glance-review-${g.id}`} />
        <Val value={updated} empty="No activity yet" testId={`biz-glance-updated-${g.id}`} />
        <Val value={inits} empty="No initiatives linked yet" testId={`biz-glance-items-${g.id}`} />
        <Val value={target} empty="No target date" strong testId={`biz-glance-target-${g.id}`} />
        <div className="flex justify-end items-center gap-2"><AvatarStack entity={g} size={30} /><ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--ink-3)' }} /></div>
      </div>
      <div className="md:hidden py-4" data-testid={`biz-glance-card-${g.id}`}>
        <div className="flex items-start justify-between gap-3">
          <GoalName g={g} h={h} p={p} st={st} sfx="-m" />
          <StatusPill st={st} testId={`biz-glance-status-${g.id}-m`} />
        </div>
        <div className="grid grid-cols-[1.4fr_1fr_auto] gap-4 mt-4">
          <div className="min-w-0"><Th>Review</Th><div className="mt-1"><ReviewCell g={g} testId={`biz-glance-review-${g.id}-m`} /></div></div>
          <div className="min-w-0"><Th>Target</Th><div className="mt-1"><Val value={target} empty="No target date" strong testId={`biz-glance-target-${g.id}-m`} /></div></div>
          <div><Th>Owner</Th><div className="mt-1.5"><AvatarStack entity={g} size={26} /></div></div>
        </div>
      </div>
    </button>
  );
}

function PlanGlance({ goals, items, setView, onOpenGoal }) {
  const { year } = useBiz();
  const live = goals.filter(isOpenGoal);
  const inYear = (g) => g.plan_year === year;
  const groups = [
    { key: 'year', label: `The big goal · ${year}`, T: TIERS.year, list: live.filter((g) => tierOf(g) === 'year' && inYear(g)) },
    { key: 'quarter', label: 'Quarterly goals', T: TIERS.quarter, list: live.filter((g) => tierOf(g) === 'quarter' && inYear(g)).sort((a, b) => a.quarter - b.quarter) },
    { key: 'mini', label: 'Mini goals', T: TIERS.mini, list: live.filter((g) => tierOf(g) === 'mini' && inYear(g)) },
    { key: 'future', label: `Not linked to the ${year} plan`, T: { ...TIERS.future, color: 'var(--honey)' }, link: true, list: live.filter((g) => tierOf(g) === 'future' || !inYear(g)) },
  ];
  return (
    <section className="biz-card p-5" data-testid="biz-plan-glance">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl">What we’re working on</h2>
        <button type="button" onClick={() => setView('plan')} className="text-xs font-semibold inline-flex items-center gap-1" style={{ color: 'var(--sage-deep)' }} data-testid="biz-glance-open-plan">Open plan <ArrowRight className="w-3 h-3" /></button>
      </div>
      {live.length === 0 && <p className="text-sm py-4 text-center" style={{ color: 'var(--ink-3)' }}>No goals yet — add the first one from the Plan tab.</p>}
      {live.length > 0 && (
        <div className={`hidden md:grid ${COLS} pb-3 mb-1 ${BLEED}`} style={{ borderBottom: '1px solid var(--line)' }} data-testid="biz-glance-header">
          {HEADERS.map((hd) => <Th key={hd} right={hd === 'Owner'}>{hd}</Th>)}
        </div>
      )}
      <div className="flex flex-col gap-5">
        {groups.filter((gr) => gr.list.length).map((gr) => (
          <div key={gr.key} data-testid={`biz-glance-group-${gr.key}`}>
            <div className="flex items-center gap-2 mb-0.5 mt-1">
              <span className="w-2 h-2 rounded-full" style={{ background: gr.T.color }} />
              <Eyebrow>{gr.label}</Eyebrow>
              {gr.link && <button type="button" onClick={() => setView('plan')} className="text-xs font-semibold" style={{ color: 'var(--honey-deep)' }} data-testid="biz-glance-link-plan">Link</button>}
            </div>
            <div>
              {gr.list.map((g, i) => <GoalRow key={g.id} g={g} items={items} h={gr.T} onOpenGoal={onOpenGoal} first={i === 0} />)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function BizOverview({ items, goals, nudge, onOpen, onOpenGoal, setView, onNewGoal, onNewItem }) {
  const open = items.filter((i) => i.status !== 'done');
  const wins = [
    ...items.filter(isWin).map((w) => ({ id: w.id, title: w.title, at: w.completed_at, go: () => onOpen(w.id) })),
    ...goals.filter((g) => g.status === 'achieved' && isWin({ status: 'done', completed_at: g.achieved_at })).map((g) => ({ id: g.id, title: `Goal: ${g.title}`, at: g.achieved_at, go: () => onOpenGoal(g.id) })),
  ].sort((a, b) => (a.at < b.at ? 1 : -1));
  const blocked = open.filter((i) => i.flags.includes('blocked')).length;
  const stale = open.filter(isStale).length;
  const quick = open.filter(isQuickWin).length;
  const live = goals.filter(isOpenGoal);
  const onTrack = live.filter((g) => g.status === 'on_track').length;
  const offTrack = live.filter((g) => g.status === 'off_track').length;
  const empty = items.length === 0 && goals.length === 0;

  if (empty) {
    return (
      <div className="biz-card p-10 text-center max-w-2xl mx-auto biz-rise" data-testid="biz-empty">
        <Compass className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--apricot-deep)' }} />
        <h2 className="text-2xl">Start with one goal</h2>
        <p className="text-sm mt-2 mb-6" style={{ color: 'var(--ink-2)' }}>Something the business should be different about in 6–12 months. Then add the initiatives that get you there, or let the AI helper suggest a first set.</p>
        <div className="flex justify-center gap-2">
          <button type="button" onClick={onNewGoal} className="biz-btn biz-btn-primary" data-testid="biz-empty-new-goal"><Plus className="w-4 h-4" /> New goal</button>
          <button type="button" onClick={() => onNewItem()} className="biz-btn" data-testid="biz-empty-new-initiative"><Plus className="w-4 h-4" /> Just a to-do</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="biz-overview">
      {wins.length === 0 ? (
        <section className="rounded-2xl px-4 flex items-center gap-2.5 h-10" style={{ border: '1px solid var(--line)', color: 'var(--ink-3)' }} data-testid="biz-wins" data-empty="true">
          <Trophy className="w-4 h-4 shrink-0" />
          <span className="text-sm truncate" data-testid="biz-wins-count">No wins yet this quarter — move a card to Done and it lands here.</span>
        </section>
      ) : (
        <section className="biz-card p-4 flex items-center gap-4 overflow-x-auto" style={{ background: 'var(--sage-soft)', borderColor: 'transparent' }} data-testid="biz-wins">
          <div className="flex items-center gap-2 shrink-0 pr-4" style={{ borderRight: '1px solid rgba(62,106,72,0.2)' }}>
            <Trophy className="w-5 h-5" style={{ color: 'var(--sage-deep)' }} />
            <div><p className="text-sm font-semibold" style={{ color: 'var(--sage-deep)' }}>Wins this quarter</p><p className="text-xs" style={{ color: 'var(--sage-deep)' }} data-testid="biz-wins-count">{wins.length} shipped</p></div>
          </div>
          {wins.map((w) => (
            <button key={w.id} type="button" onClick={w.go} className="shrink-0 text-left bg-white/70 hover:bg-white rounded-xl px-3 py-2 transition-colors" data-testid={`biz-win-${w.id}`}>
              <p className="text-xs font-medium whitespace-nowrap">{w.title}</p><p className="text-xs" style={{ color: 'var(--ink-3)' }}>{fmtDate(w.at)}</p>
            </button>
          ))}
        </section>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatTile label="Open initiatives" value={open.length} tone={open.length ? undefined : 'muted'} onClick={() => setView('board')} testId="biz-stat-open" />
        <StatTile label="Blocked" value={blocked} tone={blocked ? 'amber' : 'muted'} onClick={() => setView('board')} testId="biz-stat-blocked" />
        <StatTile label="Not reviewed 30+ days" value={stale} tone={stale ? 'amber' : 'muted'} onClick={() => setView('board')} testId="biz-stat-stale" />
        <StatTile label="Quick wins ready" value={quick} tone={quick ? 'green' : 'muted'} onClick={() => setView('quickwins')} testId="biz-stat-quick" />
        <StatTile label="Goals on track" value={`${onTrack}/${live.length}`} tone={!live.length ? 'muted' : offTrack ? 'red' : onTrack === live.length ? 'green' : 'amber'} hint={offTrack ? `${offTrack} off track` : undefined} onClick={() => setView('plan')} testId="biz-stat-goals" />
      </div>

      <Attention items={items} goals={goals} onOpen={onOpen} onOpenGoal={onOpenGoal} />

      <PlanGlance goals={goals} items={items} setView={setView} onOpenGoal={onOpenGoal} />

      <YearPlan goals={goals} compact onOpen={onOpenGoal} onNew={onNewGoal} onViewPlan={() => setView('plan')} />

      <button type="button" onClick={() => setView('settings')} className="w-full text-left flex items-center gap-3 px-4 py-3 rounded-2xl transition-colors hover:bg-white/60" style={{ border: '1px dashed var(--line-2)' }} data-testid="biz-nudge-footer">
        <Bell className="w-4 h-4" style={{ color: 'var(--apricot-deep)' }} />
        <span className="text-sm" style={{ color: 'var(--ink-2)' }}>
          {nudge.frequency === 'off' ? 'Nudges are off.' : `Next nudge ${nudge.next_due_at ? fmtDate(nudge.next_due_at, 'EEE dd/MM/yyyy') : nudge.day} at ${nudge.time}, ${nudge.frequency}.`} <span className="font-semibold" style={{ color: 'var(--ink)' }}>Adjust →</span>
        </span>
      </button>
    </div>
  );
}
